/**
 * useDataFields Hook - MA-ARCH-163
 * 
 * Extracted from ScriptEdit.jsx - Manages form fields (data collection fields) state and CRUD.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';

/**
 * Hook for managing data fields (form fields for data collection)
 * @param {string} scriptId - The script ID
 * @param {string} activeNodeId - Currently active node ID
 * @param {Function} autoSaveDraftIfDirty - Auto-save function from editor state
 * @param {boolean} canEdit - Whether user can edit
 */
export function useDataFields(scriptId, activeNodeId, autoSaveDraftIfDirty, canEdit) {
  const [formFields, setFormFields] = useState([]);
  const [dataFieldSearch, setDataFieldSearch] = useState("");

  // Add field dialog state
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldScope, setNewFieldScope] = useState("household");
  const [newFieldOptions, setNewFieldOptions] = useState("");

  // Edit field dialog state
  const [editFieldOpen, setEditFieldOpen] = useState(false);
  const [editFieldId, setEditFieldId] = useState(null);
  const [editFieldLabel, setEditFieldLabel] = useState("");
  const [editFieldType, setEditFieldType] = useState("text");
  const [editFieldRequired, setEditFieldRequired] = useState(false);
  const [editFieldScope, setEditFieldScope] = useState("household");
  const [editFieldOptions, setEditFieldOptions] = useState("");

  // Fetch form fields
  const fetchFormFields = useCallback(async () => {
    try {
      const res = await api.get(`/scripts/${scriptId}/fields`);
      setFormFields(res.data);
    } catch {
      // silent - fields are optional
    }
  }, [scriptId]);

  // Initial fetch
  useEffect(() => {
    fetchFormFields();
  }, [fetchFormFields]);

  // Derived: fields for active node
  const activeNodeFields = useMemo(() => {
    if (!activeNodeId) return [];
    return formFields.filter((f) => f.node_id === activeNodeId);
  }, [formFields, activeNodeId]);

  // Derived: filtered field options for search
  const dataFieldSearchValue = dataFieldSearch.trim().toLowerCase();
  const dataFieldOptions = useMemo(() => {
    const list = [...(formFields || [])]
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }));
    if (!dataFieldSearchValue) return list;
    return list.filter((f) => String(f.label || "").toLowerCase().includes(dataFieldSearchValue));
  }, [formFields, dataFieldSearchValue]);

  // Add field
  const onAddField = useCallback(async () => {
    if (!activeNodeId) return;
    try {
      await autoSaveDraftIfDirty?.();
      const payload = {
        node_id: activeNodeId,
        label: newFieldLabel.trim(),
        field_type: newFieldType,
        required: newFieldRequired,
        scope: newFieldScope,
        sort_order: activeNodeFields.length,
      };

      if (newFieldType === "dropdown" && newFieldOptions.trim()) {
        payload.options = newFieldOptions.split(",").map((opt) => opt.trim()).filter(Boolean);
      }

      await api.post(`/scripts/${scriptId}/fields`, payload);
      toast({ title: "Data field added" });
      setAddFieldOpen(false);
      setNewFieldLabel("");
      setNewFieldType("text");
      setNewFieldRequired(false);
      setNewFieldScope("household");
      setNewFieldOptions("");
      await fetchFormFields();
    } catch (e) {
      toast({
        title: "Add field failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [activeNodeId, newFieldLabel, newFieldType, newFieldRequired, newFieldScope, newFieldOptions, activeNodeFields.length, scriptId, autoSaveDraftIfDirty, fetchFormFields]);

  // Open edit field dialog
  const openEditField = useCallback((field) => {
    setEditFieldId(field.id);
    setEditFieldLabel(field.label || "");
    setEditFieldType(field.field_type || "text");
    setEditFieldRequired(!!field.required);
    setEditFieldScope(field.scope || "household");
    setEditFieldOptions(Array.isArray(field.options) ? field.options.join(", ") : "");
    setEditFieldOpen(true);
  }, []);

  // Update field
  const onUpdateField = useCallback(async () => {
    if (!editFieldId) return;
    try {
      await autoSaveDraftIfDirty?.();
      const payload = {
        label: editFieldLabel.trim(),
        field_type: editFieldType,
        required: editFieldRequired,
        scope: editFieldScope,
      };

      if (editFieldType === "dropdown" && editFieldOptions.trim()) {
        payload.options = editFieldOptions.split(",").map((opt) => opt.trim()).filter(Boolean);
      } else if (editFieldType === "dropdown") {
        payload.options = [];
      }

      await api.put(`/fields/${editFieldId}`, payload);
      toast({ title: "Data field updated" });
      setEditFieldOpen(false);
      setEditFieldId(null);
      await fetchFormFields();
    } catch (e) {
      toast({
        title: "Update field failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [editFieldId, editFieldLabel, editFieldType, editFieldRequired, editFieldScope, editFieldOptions, autoSaveDraftIfDirty, fetchFormFields]);

  // Delete field
  const onDeleteField = useCallback(async (fieldId) => {
    try {
      await autoSaveDraftIfDirty?.();
      await api.delete(`/fields/${fieldId}`);
      toast({ title: "Data field removed" });
      await fetchFormFields();
    } catch (e) {
      toast({
        title: "Delete field failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [autoSaveDraftIfDirty, fetchFormFields]);

  // Reset add field form
  const resetAddFieldForm = useCallback(() => {
    setNewFieldLabel("");
    setNewFieldType("text");
    setNewFieldRequired(false);
    setNewFieldScope("household");
    setNewFieldOptions("");
  }, []);

  return {
    // State
    formFields,
    dataFieldSearch,
    setDataFieldSearch,
    dataFieldOptions,
    activeNodeFields,

    // Add field state
    addFieldOpen,
    setAddFieldOpen,
    newFieldLabel,
    setNewFieldLabel,
    newFieldType,
    setNewFieldType,
    newFieldRequired,
    setNewFieldRequired,
    newFieldScope,
    setNewFieldScope,
    newFieldOptions,
    setNewFieldOptions,

    // Edit field state
    editFieldOpen,
    setEditFieldOpen,
    editFieldId,
    editFieldLabel,
    setEditFieldLabel,
    editFieldType,
    setEditFieldType,
    editFieldRequired,
    setEditFieldRequired,
    editFieldScope,
    setEditFieldScope,
    editFieldOptions,
    setEditFieldOptions,

    // Actions
    fetchFormFields,
    onAddField,
    openEditField,
    onUpdateField,
    onDeleteField,
    resetAddFieldForm,
  };
}

export default useDataFields;
